import { ref } from 'vue';
import { defineStore } from 'pinia';

export const useContentSplitterStore = defineStore(
  'contentsplitter',
  () => {
    const ROUNDING_STEP = 5;

    // Default values for focus and blur
    const focusedRightPanelSize = ref(50);
    const blurredRightPanelSize = ref(50);

    // Values for different keys
    const panelSizesByKey = ref<Record<string, { focused: number; blurred: number }>>({});

    // Helper function to round to 5-step intervals
    function roundToNearestFive(value: number): number {
      return Math.round(value / ROUNDING_STEP) * ROUNDING_STEP;
    }

    // Getter for the values of a key or default values if the key doesn't exist yet
    function getPanelSizes(key: string) {
      if (!panelSizesByKey.value[key]) {
        panelSizesByKey.value[key] = {
          focused: focusedRightPanelSize.value,
          blurred: blurredRightPanelSize.value,
        };
      }
      return panelSizesByKey.value[key];
    }

    // Setter for the focus size of a key
    function setFocusedRightPanelSizeForKey(key: string, size: number) {
      const roundedSize = roundToNearestFive(size);
      const panelSizes = getPanelSizes(key);
      panelSizes.focused = roundedSize <= panelSizes.blurred ? panelSizes.blurred : roundedSize;
    }

    // Setter for the blur size of a key
    function setBlurredRightPanelSizeForKey(key: string, size: number) {
      const roundedSize = roundToNearestFive(size);
      const panelSizes = getPanelSizes(key);
      panelSizes.blurred = roundedSize >= panelSizes.focused ? panelSizes.focused : roundedSize;
    }

    // Getter for the focus size of a key
    function getFocusedRightPanelSizeForKey(key: string): number {
      return getPanelSizes(key).focused;
    }

    // Getter for the blur size of a key
    function getBlurredRightPanelSizeForKey(key: string): number {
      return getPanelSizes(key).blurred;
    }

    // Direct setters and getters for global values (without keys)
    function setFocusedRightPanelSize(size: number) {
      const roundedSize = roundToNearestFive(size);
      focusedRightPanelSize.value =
        roundedSize <= blurredRightPanelSize.value ? blurredRightPanelSize.value : roundedSize;
    }

    function setBlurredRightPanelSize(size: number) {
      const roundedSize = roundToNearestFive(size);
      blurredRightPanelSize.value =
        roundedSize >= focusedRightPanelSize.value ? focusedRightPanelSize.value : roundedSize;
    }

    return {
      focusedRightPanelSize,
      blurredRightPanelSize,
      panelSizesByKey,
      setFocusedRightPanelSize,
      setBlurredRightPanelSize,
      getPanelSizes,
      setFocusedRightPanelSizeForKey,
      setBlurredRightPanelSizeForKey,
      getFocusedRightPanelSizeForKey,
      getBlurredRightPanelSizeForKey,
    };
  },
  {
    persist: true, // Saves values to local storage
  },
);
